import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Title3, Body1, Caption1, Spinner, Badge, Button,
  TabList, Tab, MessageBar, MessageBarBody, mergeClasses,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useCustomerDecisionsAndRisks, useConfigSettings } from '../../../api/hooks';
import type { CustomerDecisionItem, CustomerRiskItem } from '../../../api/types';

// FEAT-027 Open Decisions & Risks. Two tabs: Decisions Needed + Risks. Customer-only.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  overdue: {
    // Subtle red wash for overdue items — borderColor token isn't part of every Fluent
    // minor release so we use only background to avoid TS noise.
    backgroundColor: tokens.colorPaletteRedBackground1,
  },
  header: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
});

type TabKey = 'decisions' | 'risks';

export function CustomerDecisionsRisksPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const [tab, setTab] = useState<TabKey>('decisions');
  const { data, isLoading, error } = useCustomerDecisionsAndRisks(projectId);
  // PoTeamsEmail is read from CentralConfig — when set, decisions get a deep-link to a
  // 1:1 Teams chat with the PO. Empty hides the button (hidden, not disabled).
  const { data: configs } = useConfigSettings();
  const poEmail = configs?.find((c) => c.key === 'PoTeamsEmail')?.value?.trim() ?? '';

  const decisions = data?.decisions.decisions ?? [];
  const risks = data?.risks.risks ?? [];

  function teamsChatUrl(title: string): string {
    const users = encodeURIComponent(poEmail);
    const topic = encodeURIComponent(title);
    return `https://teams.microsoft.com/l/chat/0/0?users=${users}&topicName=${topic}`;
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Open Decisions &amp; Risks</Title2>
        <Body1 className={styles.hint}>
          Decisions we're waiting on from you and risks affecting your projects — in plain English.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project.</Body1>
      ) : isLoading ? (
        <Spinner label="Loading…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : (
        <>
          <TabList selectedValue={tab} onTabSelect={(_e, d) => setTab(d.value as TabKey)}>
            <Tab value="decisions">Decisions Needed ({decisions.length})</Tab>
            <Tab value="risks">Risks ({risks.length})</Tab>
          </TabList>

          {tab === 'decisions' ? (
            decisions.length === 0 ? (
              <Body1>Nothing waiting on you. 🎉</Body1>
            ) : (
              <div className={styles.list}>
                {decisions.map((d: CustomerDecisionItem, idx: number) => (
                  <div key={idx} className={mergeClasses(styles.item, d.overdue && styles.overdue)}>
                    <div className={styles.header}>
                      <Title3>{d.customerTitle}</Title3>
                      {d.overdue && <Badge appearance="filled" color="danger">Overdue</Badge>}
                    </div>
                    {d.customerDescription ? <Body1>{d.customerDescription}</Body1> : null}
                    <Body1><strong>What we need:</strong> {d.whatWeNeed}</Body1>
                    <Caption1>
                      Requested {new Date(d.requestedAt).toLocaleDateString()}
                      {d.dueBy ? ` · Due ${new Date(d.dueBy).toLocaleDateString()}` : ''}
                    </Caption1>
                    {poEmail && (
                      <div>
                        <Button
                          as="a"
                          appearance="primary"
                          size="small"
                          href={teamsChatUrl(d.customerTitle)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Provide Response in Teams
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            risks.length === 0 ? (
              <Body1>No high-priority risks affecting your projects right now.</Body1>
            ) : (
              <div className={styles.list}>
                {risks.map((r: CustomerRiskItem, idx: number) => (
                  <div key={idx} className={styles.item}>
                    <Title3>{r.customerTitle}</Title3>
                    {r.riskDescription ? <Body1>{r.riskDescription}</Body1> : null}
                    <Caption1>Mitigation status: {r.mitigationStatus}</Caption1>
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
